import { httpStatusOf } from '@/app/live-election/engine/capability';

import {
	BASELINE_CAPABILITIES,
	type IBulkCancellationResult,
	type ICancellationCapabilities,
	type ICancellationPage,
	type ICancellationRow,
} from '../types';

/**
 * Pure readers for api.core's cancellation responses. Kept out of the hooks so the fallbacks
 * (a 404 capabilities route, a bare array, a body with no paging fields) are unit-testable.
 */

export const base = (electionId: number) => `/api/v1/Election/${electionId}/cancellations`;

export const withParty = (path: string, partyId: number | null, params: URLSearchParams = new URLSearchParams()) => {
	if (partyId !== null && partyId > 0) params.set('partyId', String(partyId));
	const qs = params.toString();
	return qs ? `${path}?${qs}` : path;
};

/** The house envelope `{ data }`, or a bare body. */
export const unwrap = <T>(body: unknown): T => {
	if (body && typeof body === 'object') {
		const record = body as Record<string, unknown>;
		if ('data' in record && 'isSuccessful' in record) return record.data as T;
	}
	return body as T;
};

/**
 * `GET capabilities`. Only a 404/405 means "this server predates v2" and yields the v1
 * baseline (bulk POST / DELETE / GET only). Anything else is rethrown — a 500 is not proof
 * that the feature is absent, and reading it as such would hide every v2 control.
 */
export const readCapabilities = (body: unknown): ICancellationCapabilities => {
	const raw = unwrap<Record<string, unknown>>(body) ?? {};
	const flag = (k: string) => raw[k] === true;
	const minutes = raw.withdrawWindowMinutes;
	return {
		version: 'v2',
		agentReport: flag('agentReport'),
		withdrawWindowMinutes: typeof minutes === 'number' && Number.isFinite(minutes) ? minutes : null,
		review: flag('review'),
		evidence: flag('evidence'),
		impact: flag('impact'),
		liveEvents: flag('liveEvents'),
		candidateSms: flag('candidateSms'),
		resultsCancellationAware: flag('resultsCancellationAware'),
		incidentLookup: flag('incidentLookup'),
	};
};

export const isRouteAbsent = (error: unknown): boolean => {
	const s = httpStatusOf(error);
	return s === 404 || s === 405;
};

export const capabilitiesOrBaseline = (error: unknown): ICancellationCapabilities => {
	if (isRouteAbsent(error)) return BASELINE_CAPABILITIES;
	throw error;
};

const asNumber = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

export const readPage = (body: unknown, pageNumber: number, pageSize: number): ICancellationPage => {
	const top = (body ?? {}) as Record<string, unknown>;
	const data = top.data ?? body;
	let rows: ICancellationRow[] = [];
	let nested: Record<string, unknown> = {};
	if (Array.isArray(data)) rows = data as ICancellationRow[];
	else if (data && typeof data === 'object') {
		nested = data as Record<string, unknown>;
		const items = nested.items ?? nested.data ?? nested.rows;
		if (Array.isArray(items)) rows = items as ICancellationRow[];
	}
	return {
		rows,
		totalCount: asNumber(top.totalCount) ?? asNumber(nested.totalCount),
		pageNumber: asNumber(top.currentPage) ?? asNumber(top.pageNumber) ?? pageNumber,
		pageSize: asNumber(top.pageSize) ?? pageSize,
	};
};

const strings = (v: unknown): string[] =>
	Array.isArray(v)
		? v.map((x) => (typeof x === 'string' ? x : ((x as { pollingUnitCode?: string })?.pollingUnitCode ?? ''))).filter(Boolean)
		: [];

/** The bulk POST's 200 body and its all-or-nothing 400 body share this shape. */
export const readBulkResult = (body: unknown): IBulkCancellationResult => {
	const raw = unwrap<Record<string, unknown>>(body) ?? {};
	return {
		recorded: Array.isArray(raw.recorded) ? (raw.recorded as ICancellationRow[]) : [],
		alreadyCancelled: strings(raw.alreadyCancelled),
		notInElection: strings(raw.notInElection),
		unknown: strings(raw.unknown),
	};
};

/** A row is "active" unless the server SAID otherwise. A v1 row has no state and is active. */
export const isActiveRow = (row: ICancellationRow): boolean => row.state === undefined || row.state === 'Active';

/** Unreviewed only when the server says so — a v1 row with no review field is not flagged. */
export const needsReview = (row: ICancellationRow): boolean => isActiveRow(row) && row.reviewStatus === 'Unreviewed';

/** Coordinates arrive as numbers, numeric strings, or the literal `'null'`. */
export const coordsOf = (row: Pick<ICancellationRow, 'latitude' | 'longitude'>): [number, number] | null => {
	const read = (v: unknown) => {
		if (v === null || v === undefined) return NaN;
		if (typeof v === 'string' && (v.trim() === '' || v === 'null' || v === 'undefined')) return NaN;
		return Number(v);
	};
	const lat = read(row.latitude);
	const lng = read(row.longitude);
	if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
	if (lat === 0 && lng === 0) return null;
	return [lat, lng];
};

/** Refresh this long before a signed `viewUrl` lapses, so a play started now does not 403 mid-load. */
export const EVIDENCE_REFRESH_MARGIN_MS = 60_000;

/** True when the signed download URL is past (or within the margin of) its expiry. No expiry = never stale. */
export const isViewUrlStale = (
	item: { viewUrlExpiresAt?: string | null },
	now = Date.now(),
	marginMs = EVIDENCE_REFRESH_MARGIN_MS
): boolean => {
	if (!item.viewUrlExpiresAt || item.viewUrlExpiresAt === 'null') return false;
	const at = new Date(item.viewUrlExpiresAt).getTime();
	return Number.isFinite(at) && at - marginMs <= now;
};

/** ms until the earliest URL in the list needs refreshing; null when none expires. */
export const msUntilEvidenceRefresh = (
	items: { viewUrlExpiresAt?: string | null }[],
	now = Date.now(),
	marginMs = EVIDENCE_REFRESH_MARGIN_MS
): number | null => {
	let soonest: number | null = null;
	for (const item of items) {
		if (!item.viewUrlExpiresAt || item.viewUrlExpiresAt === 'null') continue;
		const at = new Date(item.viewUrlExpiresAt).getTime();
		if (!Number.isFinite(at)) continue;
		const wait = Math.max(0, at - marginMs - now);
		soonest = soonest === null ? wait : Math.min(soonest, wait);
	}
	return soonest;
};

/** `data.code` off an api.core error body (409 / 503 carry one). */
export const errorCodeOf = (error: unknown): string | undefined => {
	const body = (error as { response?: { data?: { code?: unknown; data?: { code?: unknown } } } })?.response?.data;
	const code = body?.data?.code ?? body?.code;
	return typeof code === 'string' ? code : undefined;
};
